import './assets/main.css'

import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import swsPlugin from './auth/plugin'
import { createGtm } from '@gtm-support/vue-gtm'
import { datadogRum } from '@datadog/browser-rum'
import { injectGtm } from './utils/gtm'
import App from './App.vue'
import router from './router'
import { iubendaIsConsentGiven } from './utils/iubenda'
import { useCartStore } from './stores/cart-store'
import { useAuthStore } from './stores/auth-store'

const app = createApp(App)

app.use(createPinia())
app.use(swsPlugin)
// Ensure that the router is installed after the SWS plugin is installed, as the router has a dependency on the SDK
app.use(router)

if (import.meta.env.VITE_APP_GTM_ENABLED === 'true') {
  // Initialise datalayer for injecting the GTM script and tracking page views on route changes
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  injectGtm(import.meta.env.VITE_APP_GTM_PROPERTY_ID)
}

app.use(
  createGtm({
    id: import.meta.env.VITE_APP_GTM_PROPERTY_ID,
    vueRouter: router,
    enabled: import.meta.env.VITE_APP_GTM_ENABLED === 'true',
    debug: false, // Whether or not display console logs debugs
    loadScript: false
  })
)

app.use(() => {
  let opts = {}
  if (import.meta.env.VITE_APP_DATADOG_RUM_REPLAY_SAMPLE_RATE=== 'true') {
    opts = {
      sessionSampleRate: parseInt(import.meta.env.VITE_APP_DATADOG_RUM_SAMPLE_RATE) ?? 0,
      sessionReplaySampleRate: parseInt(import.meta.env.VITE_APP_DATADOG_RUM_REPLAY_SAMPLE_RATE) ?? 0,
    }
  }
  if (import.meta.env.VITE_APP_DATADOG_RUM_ENABLE=== 'true') {
    datadogRum.init({
      applicationId: import.meta.env.VITE_APP_DATADOG_APPLICATION_ID ?? '',
      clientToken: import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN ?? '',
      site: 'datadoghq.com',
      service: 'express-checkout',
      version: import.meta.env.VITE_RUM_VERSION ?? 'unknown',
      defaultPrivacyLevel: 'mask-user-input',
      env: import.meta.env.VITE_APP_ENV_NAME,
      trackingConsent: 'not-granted',
      trackUserInteractions: true,
      trackResources: true,
      excludedActivityUrls: [],
      workerUrl: new URL('./assets/dd-worker.js', import.meta.url).href,
      allowedTracingUrls: [
        /https:\/\/*.serato.*\//,
        /http:\/\/localhost(:\d+)?/
      ],
      ...opts
    })
    
    // Check iubenda consent and set datadog RUM consent accordingly - non blocking
    iubendaIsConsentGiven().then(consent => {
      datadogRum.setTrackingConsent(consent ? 'granted' : 'not-granted')
      if (!consent) {
        return
      }

      // [TDO-1182] Identify user session by adding user ID and cart ID to Datadog RUM
      const cartStore = useCartStore()
      const authStore = useAuthStore()
      watch(
        () => cartStore.cart?.uuid,
        (uuid) => {
          if (uuid) {
            datadogRum.setGlobalContextProperty('cart_id', uuid)
          } else {
            datadogRum.removeGlobalContextProperty('cart_id')
          }
        },
        { immediate: true }
      )
      watch(
        () => authStore.user?.id,
        (id) => {
          if (authStore.isLoggedIn && id) {
            datadogRum.setUser({ id: String(id) })
          } else {
            datadogRum.clearUser()
          }
        },
        { immediate: true }
      )

    })
  }
})

app.mount('#app')
